import React from 'react';
import type { NotificationItem } from './NotificationDropdown.types';
import { getTypeIcon, getTypeBadgeClass, formatRelativeTime } from './NotificationDropdown.utils';
import * as styles from './NotificationDropdown.styles';

interface NotificationDetailModalProps {
  notification: NotificationItem | null;
  onClose: () => void;
  onMarkAsRead: (id: string) => void;
}

export const NotificationDetailModal: React.FC<NotificationDetailModalProps> = ({
  notification,
  onClose,
  onMarkAsRead,
}) => {
  if (!notification) return null;

  return (
    <div className={styles.modalBackdrop} data-testid="notification-detail-modal">
      <div className={styles.modalCard}>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <div className="flex items-center gap-2">
            <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border capitalize ${getTypeBadgeClass(notification.type)}`}>
              {notification.type}
            </span>
            <span className="text-xs text-slate-400 font-mono">
              {formatRelativeTime(notification.created_at)}
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors p-1"
            title="Close"
            data-testid="modal-close-btn"
          >
            <i className="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>

        {/* Content */}
        <div className="flex items-start gap-3 py-2">
          <div className="text-xl pt-0.5">
            <i className={getTypeIcon(notification.type)}></i>
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-white mb-2">Notification Details</h4>
            <p className="text-sm text-slate-300 leading-relaxed bg-slate-950/60 p-3 rounded-xl border border-slate-800/80">
              {notification.message}
            </p>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-800">
          {!notification.read ? (
            <button
              onClick={() => {
                onMarkAsRead(notification.id);
                onClose();
              }}
              className="px-3.5 py-1.5 text-xs font-medium text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded-xl transition-all flex items-center gap-1.5"
              data-testid="modal-mark-read-btn"
            >
              <i className="fa-solid fa-check"></i>
              <span>Mark as Read</span>
            </button>
          ) : (
            <span className="text-xs text-emerald-400 font-medium flex items-center gap-1">
              <i className="fa-solid fa-circle-check"></i> Read
            </span>
          )}

          <button
            onClick={onClose}
            className="px-4 py-1.5 text-xs font-medium text-slate-300 bg-slate-800 hover:bg-slate-700 rounded-xl transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
